import axios from './axiosConfig';
import { Quote, CreateQuoteDTO, UpdateQuoteStatusDTO, QuoteVersion } from '../types/quote.types';

// Get all quotes
export const getAllQuotes = async (): Promise<Quote[]> => {
  const response = await axios.get<Quote[]>('/quotes');
  return response.data || [];
};

// Get quotes for a specific client
export const getClientQuotes = async (clientId: string): Promise<Quote[]> => {
  const response = await axios.get<Quote[]>(`/quotes/client/${clientId}`);
  return response.data || [];
};

// Get quotes for a specific property
export const getPropertyQuotes = async (propertyId: string): Promise<Quote[]> => {
  const response = await axios.get<Quote[]>(`/quotes/property/${propertyId}`);
  return response.data || [];
};

// Get a specific quote
export const getQuote = async (id: string): Promise<Quote> => {
  const response = await axios.get<{ data: Quote }>(`/quotes/${id}`);
  return response.data.data;
};

// Create a new quote
export const createQuote = async (quoteData: CreateQuoteDTO): Promise<Quote> => {
  const response = await axios.post<{ data: Quote }>('/quotes', quoteData);
  return response.data.data;
};

// Update a quote
export const updateQuote = async (quoteId: string, quoteData: Partial<CreateQuoteDTO>): Promise<Quote> => {
  const response = await axios.put<{ data: Quote }>(`/quotes/${quoteId}`, quoteData);
  return response.data.data;
};

// Special endpoint for draft quotes that skips validation
export const updateDraftQuote = async (quoteId: string, quoteData: Partial<CreateQuoteDTO>): Promise<Quote> => {
  const response = await axios.put<{ data: Quote }>(`/quotes/${quoteId}/draft`, quoteData);
  return response.data.data;
};

// Delete a quote
export const deleteQuote = async (id: string): Promise<void> => {
  await axios.delete(`/quotes/${id}`);
};

// Send a quote to client — optional deliberate contact selection (Audit 07);
// omitting contactId uses the client's active PRIMARY contact.
export const sendQuote = async (id: string, contactId?: string): Promise<Quote> => {
  const response = await axios.post<Quote>(`/quotes/${id}/send`, contactId ? { contactId } : {});
  return response.data;
};

// Audit 12: POST /accept was retired — it wrote a non-enum status and could
// never succeed. Selection is a lifecycle transition: PUT /:id/status with
// status 'SELECTED' + selectedSupplier (see updateQuoteStatus below).

// Reject a quote
export const rejectQuote = async (id: string): Promise<Quote> => {
  const response = await axios.post<Quote>(`/quotes/${id}/reject`);
  return response.data;
};

// Auto-save function
export const autoSaveQuote = async (quoteId: string | undefined, quoteData: Partial<CreateQuoteDTO>): Promise<Quote | null> => {
  try {
    if (!quoteId) {
      // Create new quote
      const response = await axios.post<Quote>('/quotes', {
        ...quoteData,
        status: 'DRAFT',
        dateOfQuotation: new Date().toISOString()
      });
      return response.data;
    } else {
      // Update existing quote
      const response = await axios.put<Quote>(`/quotes/${quoteId}`, {
        ...quoteData,
        dateOfQuotation: quoteData.dateOfQuotation ? new Date(quoteData.dateOfQuotation).toISOString() : undefined
      });
      return response.data;
    }
  } catch (error) {
    
    throw error;
  }
};

// Confirm quote
export const confirmQuote = async (quoteId: string): Promise<Quote> => {
  const response = await axios.post<Quote>(`/quotes/${quoteId}/confirm`);
  return response.data;
};

// Print quote - uses the PDF endpoint since there's no dedicated print endpoint
export const printQuote = async (quoteId: string): Promise<Blob> => {
  try {
    const response = await axios.get(`/quotes/${quoteId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error printing quote:', error);
    throw error;
  }
};

// Update quote status
export const updateQuoteStatus = async (quoteId: string, statusData: UpdateQuoteStatusDTO): Promise<Quote> => {
  const response = await axios.put<{ data: Quote }>(`/quotes/${quoteId}/status`, statusData);
  return response.data.data;
};

// Audit 12: /select-supplier never existed server-side — the call always
// 404'd. Supplier selection is part of the SELECTED lifecycle transition
// (updateQuoteStatus with status:'SELECTED' + selectedSupplier).

// Get version history for a quote
export const getQuoteVersions = async (quoteId: string): Promise<QuoteVersion[]> => {
  const response = await axios.get<{ data: QuoteVersion[] }>(`/quotes/${quoteId}/versions`);
  return response.data.data || [];
};

// Download quote PDF
export const downloadQuotePdf = async (quoteId: string, clientName: string) => {
  try {
    const response = await axios.get(`/quotes/${quoteId}/pdf`, {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${clientName}_quote.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    
    throw error;
  }
};
